<script setup>
import TextInput from '@/Shared/TextInput.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import { useForm, router } from '@inertiajs/vue3'
import { watch } from 'vue'
import dayjs from 'dayjs'
import Portrait from '@/Shared/Portrait.vue'
import Icon from '@/Shared/Icon.vue'

const props = defineProps({
  coaches: {
    type: Array,
    default: () => []
  },
  filters: {
    type: Object,
    default: null
  },
  levels: {
    type: Array,
    default: () => []
  },
  students: {
    type: Array,
    default: () => []
  },
  weekdays: {
    type: Array,
    default: () => []
  }
})

let form = useForm({
  name: '',
  description: '',
  coach_id: props.coaches[0]?.id,
  level_id: props.filters.level_id || props.levels[0]?.id,
  time_start: '09:00',
  time_end: '10:00',
  date_start: dayjs().add(1, 'day').format('YYYY-MM-DD'),
  date_end: dayjs().add(7, 'day').format('YYYY-MM-DD'),
  weekdays: [],
  students: [],
  max_students: 4
})

watch(
  () => form.level_id,
  (level_id) => {
    form.students = []

    router.get(
      '/lessons/create',
      { level_id },
      {
        preserveState: true,
        preserveScroll: true
      }
    )
  },
  {
    immediate: true
  }
)

function store() {
  form.post('/lessons/store')
}

const breadcrumbs = [{ label: 'Clases', href: '/lessons' }, { label: 'Crear' }]
</script>

<template>
  <Portrait
    title="Crear clase"
    subtitle="Configura una nueva clase para los alumnos del club"
    :breadcrumbs="breadcrumbs"
    :footer-actions="true"
  >
    <form class="space-y-6" @submit.prevent="store">
      <div class="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-r-md">
        <div class="flex">
          <div class="flex-shrink-0">
            <Icon name="InformationCircle" class="h-5 w-5 text-blue-400" />
          </div>
          <div class="ml-3">
            <p class="text-sm text-blue-700">
              Para crear una clase, selecciona un entrenador, nivel y los días de la semana en que
              se impartirá. También puedes asignar alumnos directamente.
            </p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-6">
        <div class="bg-gray-50 p-4 rounded-md">
          <h4 class="text-sm font-medium text-gray-700 mb-4">Información básica</h4>
          <div class="grid grid-cols-1 gap-4">
            <SelectInput
              v-model="form.coach_id"
              :error="form.errors.coach_id"
              class="w-full"
              label="Entrenador"
            >
              <option v-for="coach in coaches" :key="coach.id" :value="coach.id">
                {{ coach.player.fullname }}
              </option>
            </SelectInput>

            <SelectInput
              v-model="form.level_id"
              :error="form.errors.level_id"
              class="w-full"
              label="Nivel"
            >
              <option v-for="level in levels" :key="level.id" :value="level.id">
                {{ level.name }}
              </option>
            </SelectInput>

            <TextInput
              v-model="form.max_students"
              :error="form.errors.max_students"
              class="w-full"
              label="Número máximo de alumnos"
              type="number"
            />
          </div>
        </div>

        <div class="bg-gray-50 p-4 rounded-md">
          <h4 class="text-sm font-medium text-gray-700 mb-4">Horario</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput
              v-model="form.time_start"
              :error="form.errors.time_start"
              type="time"
              class="w-full"
              label="Hora inicio"
            />
            <TextInput
              v-model="form.time_end"
              :error="form.errors.time_end"
              type="time"
              class="w-full"
              label="Hora fin"
            />
          </div>

          <div class="mt-4">
            <SelectInput
              v-model="form.weekdays"
              :error="form.errors.weekdays"
              class="w-full"
              label="Días de la semana"
              multiple
            >
              <option v-for="weekday in weekdays" :key="weekday.value" :value="weekday.value">
                {{ weekday.name }}
              </option>
            </SelectInput>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <TextInput
              v-model="form.date_start"
              :error="form.errors.date_start"
              class="w-full"
              label="Fecha inicio"
              type="date"
            />
            <TextInput
              v-model="form.date_end"
              :error="form.errors.date_end"
              class="w-full"
              label="Fecha fin"
              type="date"
            />
          </div>
        </div>

        <div class="bg-gray-50 p-4 rounded-md">
          <h4 class="text-sm font-medium text-gray-700 mb-4">Alumnos</h4>
          <SelectInput
            v-model="form.students"
            :error="form.errors.students"
            class="w-full"
            label="Selecciona alumnos"
            multiple
          >
            <option v-for="student in students" :key="student.id" :value="student.id">
              {{ student.player.fullname }}
            </option>
          </SelectInput>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="flex items-center justify-end w-full">
        <LoadingButton :loading="form.processing" class="btn-indigo" @click="store">
          Crear
        </LoadingButton>
      </div>
    </template>
  </Portrait>
</template>
